import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Export dashboard to PDF
 * @param {string} elementId - The ID of the element to capture
 * @param {string} filename - The filename for the PDF (without extension)
 * @param {object} options - Additional options
 */
export async function exportToPDF(elementId, filename = 'dashboard-report', options = {}) {
  const element = document.getElementById(elementId);
  
  if (!element) {
    throw new Error(`Element with id "${elementId}" not found`);
  }
  
  // Show loading state
  const loadingOverlay = document.createElement('div');
  loadingOverlay.id = 'pdf-export-loading';
  loadingOverlay.innerHTML = `
    <div style="
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
    ">
      <div style="
        background: #1e293b;
        padding: 24px 48px;
        border-radius: 12px;
        text-align: center;
        color: white;
      ">
        <div style="
          width: 40px;
          height: 40px;
          border: 3px solid #22c55e;
          border-top-color: transparent;
          border-radius: 50%;
          margin: 0 auto 16px;
          animation: spin 1s linear infinite;
        "></div>
        <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
        <p style="margin: 0; font-size: 16px;">Generating PDF...</p>
      </div>
    </div>
  `;
  document.body.appendChild(loadingOverlay);
  
  try {
    // Clone the element to avoid modifying the original
    const clone = element.cloneNode(true);
    clone.style.width = element.offsetWidth + 'px';
    clone.style.position = 'absolute';
    clone.style.left = '-9999px';
    clone.style.top = '0';
    clone.style.background = '#0f172a'; // Match dark background
    document.body.appendChild(clone);
    
    // Wait a bit for any animations to settle
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Capture with html2canvas
    const canvas = await html2canvas(clone, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#0f172a',
      logging: false,
      ...options.canvasOptions
    });
    
    // Remove clone
    document.body.removeChild(clone);
    
    // Calculate dimensions
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    // Create PDF
    const pdf = new jsPDF({
      orientation: imgHeight > pageHeight ? 'portrait' : 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    // Add header
    pdf.setFillColor(15, 23, 42); // slate-900
    pdf.rect(0, 0, 210, 20, 'F');
    pdf.setFontSize(16);
    pdf.setTextColor(255, 255, 255);
    pdf.text('EdFrame Dashboard Report', 10, 13);
    pdf.setFontSize(10);
    pdf.setTextColor(148, 163, 184); // slate-400
    pdf.text(new Date().toLocaleString(), 200, 13, { align: 'right' });
    
    // Add content
    let heightLeft = imgHeight;
    let position = 25; // Start below header
    
    const imgData = canvas.toDataURL('image/png');
    
    // First page
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= (pageHeight - position);
    
    // Additional pages if needed
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    
    // Save PDF
    const dateStr = new Date().toISOString().split('T')[0];
    pdf.save(`${filename}-${dateStr}.pdf`);
    
    return true;
  } catch (error) {
    console.error('PDF export failed:', error);
    throw error;
  } finally {
    // Remove loading overlay
    const overlay = document.getElementById('pdf-export-loading');
    if (overlay) {
      document.body.removeChild(overlay);
    }
  }
}

/**
 * Export a specific chart to PDF
 * @param {HTMLElement} chartElement - The chart element to capture
 * @param {string} title - Title for the PDF
 * @param {string} filename - Filename for the PDF
 */
export async function exportChartToPDF(chartElement, title, filename = 'chart') {
  if (!chartElement) {
    throw new Error('Chart element is required');
  }
  
  try {
    const canvas = await html2canvas(chartElement, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#1e293b',
      logging: false
    });
    
    const imgWidth = 180;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    const pdf = new jsPDF({
      orientation: imgHeight > 200 ? 'portrait' : 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    // Header
    pdf.setFillColor(15, 23, 42);
    pdf.rect(0, 0, pdf.internal.pageSize.getWidth(), 25, 'F');
    pdf.setFontSize(18);
    pdf.setTextColor(255, 255, 255);
    pdf.text(title, 15, 16);
    pdf.setFontSize(10);
    pdf.setTextColor(148, 163, 184);
    pdf.text(new Date().toLocaleString(), pdf.internal.pageSize.getWidth() - 15, 16, { align: 'right' });
    
    // Chart image
    const imgData = canvas.toDataURL('image/png');
    pdf.addImage(imgData, 'PNG', 15, 35, imgWidth, imgHeight);
    
    const dateStr = new Date().toISOString().split('T')[0];
    pdf.save(`${filename}-${dateStr}.pdf`);
    
    return true;
  } catch (error) {
    console.error('Chart PDF export failed:', error);
    throw error;
  }
}

